import React, { useEffect, useMemo, useState } from 'react';
import { useBetSlip, SlipItem } from '../context/BetSlipContext';
import { placeBet, queueBspSingle, queueOwnBotSingle, betfairErrorMessage, PlaceBetRequest } from '../api/betfair';
import './BetSlipSidebar.css';
import './RaceCompact.css';
import MultiplesSlip from './MultiplesSlip';
import {isPaperAccount} from '../api/paper';

const round2 = (n: number) => Math.round(n * 100) / 100;

type PlaceResult = { id: string; ok: boolean; message: string };

// ownBot: a non-owner user's horse BACK singles go into their own bot, never the owner's Auto queue.
const BetSlipSidebar: React.FC<{ ownBot?: boolean }> = ({ ownBot = false }) => {
  const {
    items, status, statusError, open,
    removeItem, updateItem, clear, setOpen, refreshStatus,
    multipleItems,clearMultiples,
  } = useBetSlip();

  const [confirm, setConfirm] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [results, setResults] = useState<PlaceResult[]>([]);

  const paper=isPaperAccount();
  const count = items.length;
  const usesBspQueue=(it:SlipItem)=>!paper&&it.side==='BACK'&&(!it.racingCode||it.racingCode==='HORSE');
  const fundedItems = items.filter(it => Number(it.stake) > 0);
  const allBsp=fundedItems.length>0&&fundedItems.every(usesBspQueue);
  useEffect(() => { setConfirm(false); }, [items]);
  const balance = status?.account.balance ?? null;
  const connected = !!status?.configured;

  const totalStake = useMemo(
    () => round2(items.reduce((s, it) => s + (Number(it.stake) || 0), 0)),
    [items]
  );
  // Money actually at risk: a BACK risks its stake; a LAY risks its liability.
  const totalRisk = useMemo(
    () => round2(items.reduce((s, it) => {
      const stake = Number(it.stake) || 0;
      const price = Number(it.price) || 0;
      return s + (it.side === 'LAY' ? stake * (price - 1) : stake);
    }, 0)),
    [items]
  );

  // While the panel is open, push the page content left (desktop) so the slip
  // doesn't overlay the runner table. Handled via a body class + CSS so the
  // sticky header reflows too. Collapsed/FAB state doesn't push anything.
  useEffect(() => {
    document.body.classList.toggle('slip-open', open);
    return () => document.body.classList.remove('slip-open');
  }, [open]);

  // Floating launcher when collapsed but the slip has selections.
  if (!open) {
    if (count === 0 && multipleItems.length === 0) return null;
    return (
      <button className="slip-fab" onClick={() => setOpen(true)}>
        🧾 Bet Slip <span className="slip-fab-count">{count+multipleItems.length}</span>
      </button>
    );
  }



  const placeAllSingles = async () => {
    setPlacing(true);
    const out: PlaceResult[] = [];
    for (const it of fundedItems) {
      try {
        const req: PlaceBetRequest = {
          marketId: it.marketId,
          selectionId: it.selectionId,
          runnerName: it.runnerName,
          side: it.side,
          size: round2(it.stake),
          price: Number(it.price),
          // Take-SP is the house default (Bet pricing spec): any portion unmatched
          // at the off is sent to the SP auction rather than lapsed. MARKET_ON_CLOSE
          // is Betfair's "Take SP". Was hardcoded to 'LAPSE' (cancel at off).
          persistenceType: it.persistenceType || 'MARKET_ON_CLOSE',
          marketName: it.marketName,
          meetingName: it.meetingName,
          raceNumber: it.raceNumber,
          date: it.date,
        };
        if(usesBspQueue(it)){
          const mt=it.marketType||(it.marketName==='PLACE'?'PLACE':'WIN');
          const queued=ownBot?await queueOwnBotSingle(req,mt):await queueBspSingle(req,mt);
          out.push({id:it.id,ok:ownBot?queued.live===true:true,message:it.runnerName+': added to '+(ownBot?'your bot':'Auto')+' at BSP, $'+Number(queued.stake).toFixed(2)+' · '+(ownBot?queued.note:queued.queue_state)});
          continue;
        }
        if (ownBot) throw new Error('Only horse racing BACK singles can be added to your bot.');
        const res = await placeBet(req);
        const b = res.bet;
        out.push({
          id: it.id,
          ok: true,
          message: paper ? `${it.runnerName}: saved to your paper account` : `${it.runnerName}: ${b.orderStatus}, matched $${b.sizeMatched.toFixed(2)}${b.averagePriceMatched ? ` @ ${b.averagePriceMatched.toFixed(2)}` : ''}`,
        });
      } catch (err) {
        out.push({ id: it.id, ok: false, message: `${it.runnerName}: ${betfairErrorMessage(err)}` });
      }
    }
    setResults(out);
    setPlacing(false);
    setConfirm(false);
    // Drop the selections that placed successfully; keep failures for retry.
    const failed = new Set(out.filter((r) => !r.ok).map((r) => r.id));
    fundedItems.filter((it) => !failed.has(it.id)).forEach((it) => removeItem(it.id));
    // Any matched stake reduces available funds, so re-read the live Betfair
    // balance once placement finishes (it was only fetched on mount before).
    if (out.some((r) => r.ok)) refreshStatus();
  };

  const renderItem = (it: SlipItem) => {
    const stake = Number(it.stake) || 0;
    const price = Number(it.price) || 0;
    const isLay = it.side === 'LAY';
    const ret = round2(isLay ? stake * (price - 1) : stake * price);
    const result = results.find((r) => r.id === it.id);
    return (
      <div className="slip-item" key={it.id}>
        <div className="slip-item-head">
          <span className={`slip-bettype ${isLay ? 'lay' : 'back'}`}>{it.side}</span>
          <div className="slip-item-name">
            <strong>{it.number ? `${it.number}. ` : ''}{it.runnerName}</strong>
            <span className="slip-item-race">
              {it.meetingName}{it.raceNumber ? ` · R${it.raceNumber}` : ''} · {it.marketType || it.marketName || 'Exchange'}{it.persistenceType==='LAPSE'?' · Limit order · Unmatched lapses at start':''}
            </span>
          </div>
          <button className="slip-remove" aria-label="Remove" onClick={() => removeItem(it.id)}>×</button>
        </div>

        <div className="slip-item-fields">
          <label className="slip-field">
            <span>Odds</span>
            <div className="slip-stepper">
              <button type="button" onClick={() => updateItem(it.id, { price: round2(Math.max(1.01, it.price - 0.05)) })}>−</button>
              <input
                type="number" step="0.01" min="1.01"
                value={it.price}
                onChange={(e) => updateItem(it.id, { price: parseFloat(e.target.value) || 0 })}
              />
              <button type="button" onClick={() => updateItem(it.id, { price: round2(it.price + 0.05) })}>+</button>
            </div>
          </label>
          <label className="slip-field">
            <span>Stake $</span>
            <div className="slip-stepper">
              <button type="button" onClick={() => updateItem(it.id, { stake: round2(Math.max(0, it.stake - 5)) })}>−</button>
              <input
                type="number" step="1" min="0"
                value={it.stake}
                onChange={(e) => updateItem(it.id, { stake: parseFloat(e.target.value) || 0 })}
              />
              <button type="button" onClick={() => updateItem(it.id, { stake: round2(it.stake + 5) })}>+</button>
            </div>
          </label>
        </div>

        <div className="slip-quick">
          {[5, 10, 25, 50].map((q) => (
            <button key={q} type="button" onClick={() => updateItem(it.id, { stake: q })}>${q}</button>
          ))}
          <span className="slip-item-return">
            {isLay ? 'Liability' : 'Returns'} <strong>${ret.toFixed(2)}</strong>
          </span>
        </div>

        {result && (
          <div className={`slip-item-result ${result.ok ? 'ok' : 'err'}`}>{result.message}</div>
        )}
      </div>
    );
  };

  return (
    <aside className="bet-slip-sidebar unified-slip" aria-label="Bet slip">
      <div className="slip-header">
        <h3>Bet Slip {count > 0 && <span className="slip-count">{count}</span>}</h3>
        <div className="slip-header-actions">
          {count > 0 && <button className="slip-link" onClick={()=>{clear();clearMultiples();}}>Clear</button>}
          <button className="slip-close" aria-label="Collapse" onClick={() => setOpen(false)}>×</button>
        </div>
      </div>


      <div className={`slip-status ${connected ? 'ok' : 'off'}`}>
        <span className="slip-status-dot" />
        {connected ? (
          <>{paper?'Paper account · Available':'Betfair connected · Balance'} <strong>${balance != null ? balance.toFixed(2) : '—'}</strong></>
        ) : (
          <>Betfair not connected{statusError ? ` — ${statusError}` : ''}</>
        )}
      </div>

      <div className="slip-scroll">
      <h4 className="slip-section-title">Selections · Singles</h4>
      {count === 0 ? (
        <div className="slip-empty">
          <p>Your bet slip is empty.</p>
          <p className="slip-empty-hint">Select a blue Win or Place price. Leave single stakes at zero to build only a multiple.</p>
        </div>
      ) : (
        <>
          <div className="slip-body">
            {items.map(renderItem)}
          </div>

          <div className="slip-footer">
            <div className="slip-totals">
              <div><span>Total stake</span><strong>${totalStake.toFixed(2)}</strong></div>
              <div><span>At risk</span><strong>${totalRisk.toFixed(2)}</strong></div>
            </div>

            {!connected && (
              <div className="slip-warn">Connect Betfair to place bets.</div>
            )}
            {connected && balance != null && totalRisk > balance && (
              <div className="slip-warn">Amount at risk exceeds your balance (${balance.toFixed(2)}).</div>
            )}

            {!confirm ? (
              <button
                className="slip-place"
                disabled={!connected || placing || totalStake <= 0}
                onClick={() => allBsp ? void placeAllSingles() : setConfirm(true)}
              >
                {placing ? 'Adding… ' : ''}{allBsp?'Add to Auto · BSP ·':'Place'} {fundedItems.length} single{fundedItems.length > 1 ? 's' : ''} · ${totalStake.toFixed(2)}
              </button>
            ) : (
              <div className="slip-confirm">
                {allBsp?<p>Add <strong>{fundedItems.length}</strong> BACK single{fundedItems.length>1?'s':''} to the live Auto queue for <strong>${totalStake.toFixed(2)}</strong> at BSP. Selected odds are indicative, not a minimum price. Auto running/armed controls apply. Normal submission is 5 minutes before the race; late additions are checked at 1 minute.</p>:<p>⚠️ This places <strong>{fundedItems.length}</strong> real-money bet{fundedItems.length > 1 ? 's' : ''} (Back/Lay) on the Betfair Exchange — <strong>${totalRisk.toFixed(2)}</strong> at risk.</p>}
                <div className="slip-confirm-actions">
                  <button className="slip-btn-ghost" disabled={placing} onClick={() => setConfirm(false)}>Back</button>
                  <button className="slip-btn-danger" disabled={placing} onClick={placeAllSingles}>
                    {placing ? 'Submitting…' : allBsp?'Confirm & add to Auto':'Confirm & place'}
                  </button>
                </div>
              </div>
            )}

            {results.filter(r=>r.ok).map(r=><p key={r.id} role="status" className="slip-item-result ok">{r.message}</p>)}
            {results.length > 0 && !confirm && (
              <button className="slip-link slip-clear-results" onClick={() => setResults([])}>
                Dismiss results
              </button>
            )}
          </div>
        </>
      )}
      {count===0&&results.map(r=><p key={r.id} role={r.ok?'status':'alert'} className={'slip-item-result '+(r.ok?'ok':'err')}>{r.message}</p>)}
      <MultiplesSlip embedded />
      </div>
    </aside>
  );
};

export default BetSlipSidebar;

